import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const STEPS = [
  { num: "01", title: "Tell us about your business.", body: "A short call and a simple brief so we understand your services, customers and goals." },
  { num: "02", title: "We build it.", body: "Design, copy, systems and hosting, handled end-to-end in-house." },
  { num: "03", title: "You launch.", body: "We ship it, monitor it, and improve it as your business grows." },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const lineHeight = useTransform(scrollYProgress, [0.15, 0.85], ["0%", "100%"]);
  return (
    <section id="process" ref={ref} data-testid="process-section" className="relative bg-[color:var(--ww-cream)] py-20 md:py-28">
      <div className="max-w-[92rem] mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.28em] text-black/50 font-medium font-body">Process</p>
          <h2 className="mt-6 font-display font-bold uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.94] tracking-tight">
            Three steps. <span className="text-brand-gradient">One studio.</span>
          </h2>
        </div>
        <div className="relative mt-14 md:mt-20 pl-8 md:pl-16">
          <div className="absolute left-2 md:left-4 top-2 bottom-2 w-px bg-black/10" />
          <motion.div style={{ height: lineHeight }} className="absolute left-2 md:left-4 top-2 w-px bg-gradient-to-b from-teal-500 to-emerald-500" />
          <div className="space-y-14 md:space-y-20">
            {STEPS.map((s, i) => <StepRow key={s.num} step={s} index={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepRow({ step, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.9", "end 0.4"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -10]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.2, 1, 1]);
  return (
    <motion.div ref={ref} style={{ y, opacity }} data-testid={`process-step-${index}`}
      className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-baseline">
      <span className="absolute -left-[26px] md:-left-[42px] top-4 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 ring-4 ring-[color:var(--ww-cream)]" />
      <div className="md:col-span-2"><span className="font-mono text-sm text-black/45">{step.num}</span></div>
      <div className="md:col-span-6">
        <h3 className="font-display font-bold uppercase text-3xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[color:var(--ww-charcoal)]">{step.title}</h3>
      </div>
      <div className="md:col-span-4 md:pt-6">
        <p className="text-base text-black/60 leading-relaxed max-w-md font-body">{step.body}</p>
      </div>
    </motion.div>
  );
}
